import { ArrowForward } from 'assets/icons';
import Button from 'components/common/Button';
import { inRange, last, get } from 'lodash';
import { useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { formatValue } from 'utils/helpers';
import Table, { type ColumnType } from 'rc-table';
import cn from 'classnames';
import EnergyScaleHouseImg from 'assets/images/energy-scale-house.svg?react';
import { Hidden } from 'components/common/Grid';
import useResizeObserver from '@react-hook/resize-observer';
import requireValue from 'utils/requireValue';
import type { TCO2CalculationResult, TCO2Calculations, TFormatMessage } from '../types';
import energyScaleClasses, { type IEnergyScaleClass } from './energy-scale-classes';
import s from './EnergyClassesAndIndicators.module.scss';

type TEnergyScaleClassName = Pick<IEnergyScaleClass, 'label' | 'color' | 'index'>;

interface IActualEnergyScaleClassProps {
  isActualEnergyClass: boolean;
  color: string;
  label?: string;
}

interface IEnergyScaleRow {
  key: string;
  energyClassName: TEnergyScaleClassName;
  actualEnergyClass: IActualEnergyScaleClassProps;
  afterEnergyClass: IActualEnergyScaleClassProps;
}

interface IGetColumnsArgs {
  t: TFormatMessage;
  costOverviewTab: boolean;
}

interface IEnergyScaleHouseProps {
  containerTableRef: RefObject<HTMLDivElement>;
  t: TFormatMessage;
}

interface IEnergyScaleClassesProps {
  t: TFormatMessage;
  co2Calculations: TCO2Calculations;
  costOverviewTab?: boolean;
}

interface IHouseEnergyIndicatorsProps {
  t: TFormatMessage;
  nextStep?: () => void;
  calculations: TCO2CalculationResult;
}

interface IEnergyClassesAndIndicatorsProps {
  t: TFormatMessage;
  co2Calculations: TCO2Calculations;
  nextStep?: () => void;
}

// Number(): the comparisons and inRange coerce like the former code (null -> 0; a missing min or
// max -> NaN, which inRange reads as 0 like undefined).
// Bug: a demand of exactly 251 (or NaN) is in no class, and the render crashes
// reading `index` of undefined.
function getEnergyClass(specificHeatingDemand: number | null) {
  let energyClass: IEnergyScaleClass | undefined;
  const lastEnergyClass = requireValue(last(energyScaleClasses), 'min');
  if (Number(specificHeatingDemand) < Number(energyScaleClasses[0].max)) {
    [energyClass] = energyScaleClasses;
  } else if (Number(specificHeatingDemand) > Number(lastEnergyClass.min)) {
    energyClass = lastEnergyClass;
  } else {
    energyClass = energyScaleClasses.find(({ max, min }) =>
      inRange(Number(specificHeatingDemand), Number(min), Number(max) + 1),
    );
  }
  return energyClass;
}

function EnergyScaleClass({ label, color, index }: TEnergyScaleClassName) {
  const width = useMemo(() => `${20 + index * index}%`, [index]);
  return (
    <div key={label} className={s.energyClass} style={{ width, backgroundColor: color }}>
      {label}
      <div className={s.triangle} style={{ borderColor: color }} />
    </div>
  );
}

function ActualEnergyScaleClass({
  isActualEnergyClass,
  color,
  label,
}: IActualEnergyScaleClassProps) {
  if (!isActualEnergyClass) return null;
  return (
    <div style={{ backgroundColor: color }} className={s.actualEnergyClass}>
      <div className={s.triangle} style={{ borderColor: color }} />
      {label ? <div className={s.label}>{label}</div> : null}
    </div>
  );
}

function getColumns({ t, costOverviewTab }: IGetColumnsArgs) {
  const columns: ColumnType<IEnergyScaleRow>[] = [
    {
      title: '',
      key: 'energyClassName',
      dataIndex: 'energyClassName',
      // An element, not the component itself: rc-table calls `render` as a plain function inside its
      // Cell and caches the result, so a hook in a component passed directly would run in Cell's
      // hook list on the first render only and crash Cell on the next one (hover).
      render: (props: TEnergyScaleClassName) => <EnergyScaleClass {...props} />,
    },
    {
      title: costOverviewTab
        ? t({ id: 'renovation_house_wizard.house_energy_balance.before' })
        : '',
      key: 'actualEnergyClass',
      dataIndex: 'actualEnergyClass',
      width: '25%',
      render: (props: IActualEnergyScaleClassProps) => (
        <ActualEnergyScaleClass
          {...props}
          label={
            !costOverviewTab
              ? t({ id: 'renovation_house_wizard.house_energy_balance.your_house' })
              : ''
          }
        />
      ),
    },
  ];
  const columnAfterRenovations: ColumnType<IEnergyScaleRow> = {
    title: t({ id: 'renovation_house_wizard.house_energy_balance.after' }),
    key: 'afterEnergyClass',
    dataIndex: 'afterEnergyClass',
    width: '25%',
    render: (props: IActualEnergyScaleClassProps) => <ActualEnergyScaleClass {...props} />,
  };

  if (costOverviewTab) columns.push(columnAfterRenovations);

  return columns;
}

const useSize = (target: RefObject<HTMLDivElement>) => {
  const [size, setSize] = useState<DOMRectReadOnly>();

  useLayoutEffect(() => {
    if (target.current) {
      setSize(target.current.getBoundingClientRect());
    }
  }, [target]);

  useResizeObserver(target, (entry: ResizeObserverEntry) => setSize(entry.contentRect));
  return size;
};

const EnergyScaleHouse = ({ containerTableRef, t }: IEnergyScaleHouseProps) => {
  const size = useSize(containerTableRef);

  const height = get(size, 'height', 0);
  const width = get(size, 'width', 0);

  return (
    <Hidden xs sm>
      <EnergyScaleHouseImg
        title={t({ id: 'alt_text.renovation_house.energy_scale_house' })}
        style={{
          height: `${height}px`,
          minHeight: `${height}px`,
          maxHeight: `${height}px`,
          width,
          marginRight: '10px',
        }}
      />
    </Hidden>
  );
};

export const EnergyScaleClasses = ({
  t,
  co2Calculations,
  costOverviewTab = false,
}: IEnergyScaleClassesProps) => {
  const containerTableRef = useRef<HTMLDivElement>(null);
  const { co2_emissions_before, co2_emissions_after } = co2Calculations;
  const specificHeatingDemandBefore = get(
    co2_emissions_before,
    'result.specific_heating_demand',
    null,
  );
  const specificHeatingDemandAfter = get(
    co2_emissions_after,
    'result.specific_heating_demand',
    null,
  );

  const actualEnergyClass = useMemo(
    () => getEnergyClass(specificHeatingDemandBefore),
    [specificHeatingDemandBefore],
  );
  const energyClassAfterRenovations = useMemo(
    () => getEnergyClass(specificHeatingDemandAfter),
    [specificHeatingDemandAfter],
  );

  const columns = useMemo(() => getColumns({ t, costOverviewTab }), [t, costOverviewTab]);

  const actualEnergyClassIndex = requireValue(actualEnergyClass, 'index').index;
  const energyClassAfterRenovationsIndex = requireValue(energyClassAfterRenovations, 'index').index;
  const data = useMemo(
    () =>
      energyScaleClasses.map(({ label, color, index }): IEnergyScaleRow => ({
        key: label,
        energyClassName: { label, color, index },
        actualEnergyClass: { isActualEnergyClass: index === actualEnergyClassIndex, color },
        afterEnergyClass: {
          isActualEnergyClass: index === energyClassAfterRenovationsIndex,
          color,
        },
      })),
    [actualEnergyClassIndex, energyClassAfterRenovationsIndex],
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
      }}
    >
      <div ref={containerTableRef} style={{ width: '100%', order: 1 }}>
        <Table
          className={cn(s.energyScaleTable, !costOverviewTab ? s.withoutGrid : '')}
          direction="ltr"
          columns={columns}
          data={data}
          emptyText="No data"
        />
      </div>
      {!costOverviewTab ? <EnergyScaleHouse {...{ containerTableRef, t }} /> : null}
    </div>
  );
};

function HouseEnergyIndicators({ t, nextStep, calculations }: IHouseEnergyIndicatorsProps) {
  return (
    <div className={s.houseEnergyIndicators}>
      <h3 className={s.label}>{t({ id: 'renovation_house_wizard.house_energy_balance.label' })}</h3>
      <b className={s.heatingDemandValue}>
        {t(
          { id: 'renovation_house_wizard.house_energy_balance.specific_heating_demand' },
          { value: formatValue(calculations.specific_heating_demand) },
        )}
      </b>
      <div>
        <p>
          {t(
            { id: 'renovation_house_wizard.house_energy_balance.heating' },
            { value: formatValue(calculations.heating_ghg_emission) },
          )}
        </p>
        <p>
          {t(
            { id: 'renovation_house_wizard.house_energy_balance.hot_water' },
            { value: formatValue(calculations.hot_water_ghg_emission) },
          )}
        </p>
        <p>
          {t(
            { id: 'renovation_house_wizard.house_energy_balance.electricity' },
            { value: formatValue(calculations.ghg_balance_of_power_consumption) },
          )}
        </p>
      </div>
      <div className={s.btnWrapper}>
        <Button onClick={nextStep} color="secondary" endIcon={<ArrowForward />}>
          {t({ id: 'renovation_house_wizard.improve_house_btn_label' })}
        </Button>
      </div>
    </div>
  );
}

const EnergyClassesAndIndicators = ({
  t,
  co2Calculations,
  nextStep,
}: IEnergyClassesAndIndicatorsProps) => {
  const { co2_emissions_before } = co2Calculations;

  return (
    <Row className={s.houseEnergySection}>
      <Col md={12}>
        <Row top="md" between="md">
          <Col lg={8}>
            <EnergyScaleClasses {...{ co2Calculations, t }} />
          </Col>
          <Col lg={4}>
            <HouseEnergyIndicators
              {...{ t, nextStep, calculations: co2_emissions_before.result }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default EnergyClassesAndIndicators;
